import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import { theme as colorTheme } from "../../../../Convoze.theme";

const CATEGORY_ORDER = [
  "Compliance Critical",
  "Customer Critical",
  "Communication Skills",
  "Call Handling",
  "Business Critical",
  "Fatal",
];

const positive = colorTheme("palette", "positive").value;
const negative = colorTheme("palette", "negative").value;
const primary = colorTheme("palette", "primary").value;

const statusLabel = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "fatal") return "Fatal";
  if (["fail", "false", "yes/fatal"].includes(normalized)) return "Fail";
  if (["pass", "true", "yes"].includes(normalized)) return "Pass";
  return "N/A";
};

const statusColor = (label) => {
  if (label === "Pass") return positive;
  if (label === "Fail" || label === "Fatal") return negative;
  return "#999";
};

const worstStatus = (rows) => {
  const labels = rows.map((row) => statusLabel(row?.result));
  if (labels.includes("Fatal")) return "Fatal";
  if (labels.includes("Fail")) return "Fail";
  if (labels.includes("Pass")) return "Pass";
  return "N/A";
};

const formatScore = (param) => {
  if (param?.rating === null || param?.rating === undefined) return "N/A";
  if (param?.wasNA || String(param?.rating).trim().toUpperCase() === "N/A") return "N/A";
  if (param?.maxScore === null || param?.maxScore === undefined) return param.rating;
  return `${param.rating}/${param.maxScore}`;
};

const rowObtainedScore = (row) => {
  if (row?.wasNA || String(row?.rating).trim().toUpperCase() === "N/A") {
    return Number(row?.maxScore) || 0;
  }
  return Number(row?.rating) || 0;
};

const scoreFraction = (rows) => {
  const scored = rows.filter((row) => row?.maxScore !== null && row?.maxScore !== undefined);
  const obtained = scored.reduce((sum, row) => sum + rowObtainedScore(row), 0);
  const total = scored.reduce((sum, row) => sum + (Number(row?.maxScore) || 0), 0);
  return total ? `${obtained}/${total}` : "N/A";
};

const groupByParameter = (subParameters = []) => {
  const groups = [];
  const indexByParameter = new Map();

  subParameters.forEach((item) => {
    const parameter = item?.parameter || "N/A";
    if (!indexByParameter.has(parameter)) {
      indexByParameter.set(parameter, groups.length);
      groups.push({ parameter, rows: [] });
    }
    groups[indexByParameter.get(parameter)].rows.push(item);
  });

  return groups;
};

const StatusChip = ({ value }) => {
  const label = statusLabel(value);
  const color = statusColor(label);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        border: "1px solid",
        borderColor: color,
        color,
        bgcolor: "transparent",
        fontWeight: 600,
      }}
    />
  );
};

const StatusBadge = ({ label }) => {
  const color = statusColor(label);
  return (
    <Box
      sx={{
        px: 3,
        py: 0.5,
        border: `1px solid ${color}`,
        borderRadius: 2,
        color,
        fontWeight: "bold",
      }}
    >
      {label}
    </Box>
  );
};

const SummaryStat = ({ label, value, color }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 160,
      px: 3,
      py: 1.5,
      border: `1px solid ${color}`,
      borderRadius: 2,
      textAlign: "center",
    }}
  >
    <Typography variant="caption" sx={{ color, fontWeight: 600, letterSpacing: 0.4 }}>
      {label.toUpperCase()}
    </Typography>
    <Typography variant="h6" sx={{ color, fontWeight: "bold" }}>
      {value}
    </Typography>
  </Box>
);

const overallSummary = (entries) => {
  const rows = entries
    .filter(([category]) => category !== "Fatal")
    .flatMap(([, section]) => section?.subParameters || []);
  const scored = rows.filter((row) => row?.maxScore !== null && row?.maxScore !== undefined);
  const obtained = scored.reduce((sum, row) => sum + rowObtainedScore(row), 0);
  const total = scored.reduce((sum, row) => sum + (Number(row?.maxScore) || 0), 0);
  const passed = rows.filter((row) => statusLabel(row?.result) === "Pass").length;
  const failed = rows.filter((row) => statusLabel(row?.result) === "Fail").length;
  const percentage = total ? Math.round((obtained / total) * 100) : null;
  return { obtained, total, passed, failed, percentage };
};

const KiwiQaReport = ({ data }) => {
  const entries = CATEGORY_ORDER
    .filter((category) => data?.[category])
    .map((category) => [category, data[category]]);

  if (!entries.length) {
    return (
      <Box sx={{ textAlign: "center", py: 10 }}>
        <Typography variant="h6" color="text.secondary">
          No Kiwi QA data available
        </Typography>
      </Box>
    );
  }

  const summary = overallSummary(entries);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={3}>
        <SummaryStat
          label="Total Score"
          value={summary.total ? `${summary.obtained}/${summary.total} (${summary.percentage}%)` : "N/A"}
          color={primary}
        />
        <SummaryStat label="Total Passed" value={summary.passed} color={positive} />
        <SummaryStat label="Total Failed" value={summary.failed} color={negative} />
      </Stack>

      {entries.map(([category, section]) => {
        const rows = section?.subParameters || [];
        const categoryStatus = worstStatus(rows);

        return (
          <Box
            key={category}
            sx={{ borderRadius: 4, border: "1px solid #EEEEEE", p: 3, mb: 3 }}
          >
            <Stack
              direction="row"
              gap={2}
              mb={2}
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography
                variant="body1"
                fontWeight={600}
                color={statusColor(categoryStatus)}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {category}
                <Tooltip title={section?.criticality || ""}>
                  <InfoIcon sx={{ fontSize: 18, color: "grey", cursor: "pointer" }} />
                </Tooltip>
              </Typography>
              <StatusBadge label={categoryStatus} />
            </Stack>

            {groupByParameter(rows).map((group) => (
              <Accordion key={`${category}-${group.parameter}`}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body1" sx={{ flexGrow: 1 }}>
                    {group.parameter}
                  </Typography>
                  <Typography sx={{ mr: 3 }}>
                    Score: <strong>{scoreFraction(group.rows)}</strong>
                  </Typography>
                  <Box sx={{ mr: 3 }}>
                    <StatusChip value={worstStatus(group.rows)} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Sub-Parameter</strong></TableCell>
                          <TableCell><strong>Description</strong></TableCell>
                          <TableCell><strong>Error Reason</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Score</strong></TableCell>
                          <TableCell><strong>Reason / Evidence</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.rows.map((param, index) => (
                          <TableRow key={`${category}-${group.parameter}-${param?.subParameter || param?.parameter}-${index}`}>
                            <TableCell>{param?.subParameter || param?.parameter || "N/A"}</TableCell>
                            <TableCell>{param?.description || param?.expectation || "N/A"}</TableCell>
                            <TableCell>{param?.markdownReason || "N/A"}</TableCell>
                            <TableCell><StatusChip value={param?.result} /></TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{formatScore(param)}</TableCell>
                            <TableCell>{param?.reason || "N/A"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

export default KiwiQaReport;
